import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDashboardEvents } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as leadsService from '../services/leadsService';
import LeadDetailModal from '../components/Leads/LeadDetailModal';
import LeadActionsModal from '../components/Leads/LeadActionsModal';
import LeadPipelineFilterBar, { getDefaultLeadPipelineFilters } from '../components/Leads/LeadPipelineFilterBar';
import ExportLeadsModal from '../components/ExportLeadsModal';
import { leadsKeys } from '../hooks/useLeads';
import { leadDetailKeys } from '../hooks/useLeadDetail';

const { FiUsers, FiMail, FiPhone, FiCalendar, FiEdit2, FiCheck, FiX, FiCheckCircle, FiDownload } = FiIcons;

const mapLeadFromRow = (row) => {
  const asl = row.agent_sharing_links ?? null;
  const sharingLink = Array.isArray(asl) ? asl[0] : asl;
  return {
    id: row.id,
    eventId: row.event_id,
    eventTitle: row.events?.title,
    fullName: row.full_name ?? row.name ?? '',
    name: row.full_name ?? row.name ?? '',
    email: row.email ?? '',
    phone: row.phone ?? '',
    status: row.status ?? 'new',
    notes: row.notes ?? '',
    registeredAt: row.registered_at ?? row.created_at,
    createdAt: row.created_at ?? row.registered_at,
    agentId: row.agent_id,
    primaryAgentId: row.primary_agent_id,
    archived: row.archived ?? false,
    paymentStatus: row.payment_status ?? 'pending',
    paymentAmount: row.payment_amount != null ? Number(row.payment_amount) : null,
    paymentDate: row.payment_date ?? null,
    accountCreated: row.account_created ?? false,
    accountCreatedAt: row.account_created_at ?? null,
    studentId: row.student_id ?? null,
    invitedBy: row.invited_by ?? row.invitedBy ?? null,
    sharingLinkId: row.sharing_link_id ?? null,
    sharingLinkSlug: sharingLink?.slug ?? row.sharingLinkSlug ?? null,
    sharingLinkIdentifier: sharingLink?.identifier ?? row.sharingLinkIdentifier ?? null,
    eventPrice: row.events?.price != null ? Number(row.events.price) : null
  };
};

/** Display-only: treat event as free when list price is 0 (matches Registration payment gate). */
const resolveLeadEventPrice = (lead, eventsList) => {
  const eventId = lead.eventId ?? lead.event_id;
  const fromDashboard = eventsList.find((e) => e.id === eventId);
  if (fromDashboard && fromDashboard.price != null) {
    return Number(fromDashboard.price);
  }
  if (lead.eventPrice != null) return Number(lead.eventPrice);
  return null;
};

const isLeadEventFree = (lead, eventsList) => {
  const p = resolveLeadEventPrice(lead, eventsList);
  if (p === null) return false;
  return p <= 0;
};

/** Display-only labels for the payment chip; does not change DB `payment_status`. */
const getLeadPaymentBadge = (lead, eventsList) => {
  if (lead.paymentStatus === 'paid') {
    return {
      label: lead.paymentAmount != null ? `Paid $${Number(lead.paymentAmount).toFixed(2)}` : 'Paid',
      className: 'bg-emerald-100 text-emerald-800'
    };
  }
  if (lead.paymentStatus === 'refunded') {
    return { label: 'Refunded', className: 'bg-amber-100 text-amber-800' };
  }
  if (lead.paymentStatus === 'failed') {
    return { label: 'Failed', className: 'bg-red-100 text-red-800' };
  }
  if (isLeadEventFree(lead, eventsList)) {
    return { label: 'Free', className: 'bg-teal-100 text-teal-800' };
  }
  return { label: 'Pending Payment', className: 'bg-slate-100 text-slate-700' };
};

const LeadPipeline = () => {
  const {
    leads,
    updateLead,
    events,
    fetchDashboardData,
    loading,
    error,
    patchLead,
    removeLeadFromList,
    upsertLeadInList
  } = useDashboardEvents();
  const { user, role, profile } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailLeadId, setDetailLeadId] = useState(null);
  const [detailDefaultTab, setDetailDefaultTab] = useState('details');
  const [actionsLeadId, setActionsLeadId] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [pipelineFilters, setPipelineFilters] = useState(() =>
    leadsService.loadLeadPipelineFiltersFromSession()
  );
  const [agentFilterOptions, setAgentFilterOptions] = useState([]);
  const [agentsFilterLoading, setAgentsFilterLoading] = useState(false);
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingNotes, setMeetingNotes] = useState('');
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedLeads, setArchivedLeads] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const authOpts = useMemo(
    () => ({
      role: profile?.role ?? role,
      userId: profile?.id ?? user?.id
    }),
    [profile?.role, profile?.id, role, user?.id]
  );

  const userLeads = showArchived
    ? archivedLeads.filter(lead => role === 'admin' || lead.agentId === user?.id)
    : leads.filter(lead => role === 'admin' || lead.agentId === user?.id);

  useEffect(() => {
    const leadId = searchParams.get('leadId');
    if (!leadId) return;
    setDetailLeadId(leadId);
    setDetailDefaultTab(searchParams.get('tab') === 'surveys' ? 'surveys' : 'details');
    setShowMeetingModal(false);
  }, [searchParams]);

  const openLeadDetails = (lead, tab = 'details') => {
    setDetailLeadId(lead.id);
    setDetailDefaultTab(tab);
    setShowMeetingModal(false);
    setSearchParams(tab === 'surveys' ? { leadId: lead.id, tab: 'surveys' } : { leadId: lead.id });
  };

  const closeLeadDetails = () => {
    setDetailLeadId(null);
    setDetailDefaultTab('details');
    if (searchParams.get('leadId')) {
      setSearchParams({});
    }
  };

  const invalidateLeadQueries = useCallback(
    (leadId) => {
      queryClient.invalidateQueries({ queryKey: leadsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: leadsKeys.details() });
      if (leadId) {
        queryClient.invalidateQueries({ queryKey: leadDetailKeys.lead(leadId, authOpts) });
        queryClient.invalidateQueries({ queryKey: leadDetailKeys.activities(leadId, authOpts) });
      }
    },
    [queryClient, authOpts]
  );

  const syncLeadInPipelineList = useCallback(
    async (leadId) => {
      if (!leadId) return;
      const { data, error: fetchError } = await leadsService.getLeadById(leadId, authOpts);
      if (fetchError || !data) return;
      const mapped = mapLeadFromRow(data);
      patchLead(leadId, {
        status: mapped.status,
        agentId: mapped.agentId,
        primaryAgentId: mapped.primaryAgentId,
        archived: mapped.archived,
        fullName: mapped.fullName,
        name: mapped.name,
        email: mapped.email,
        phone: mapped.phone,
        notes: mapped.notes
      });
    },
    [authOpts, patchLead]
  );

  const handleLeadDetailStatusChange = useCallback(() => {
    if (!detailLeadId) return;
    invalidateLeadQueries(detailLeadId);
    void syncLeadInPipelineList(detailLeadId);
  }, [detailLeadId, invalidateLeadQueries, syncLeadInPipelineList]);

  const handleLeadActionComplete = useCallback(
    async ({ leadId, removed } = {}) => {
      if (!leadId) return;
      invalidateLeadQueries(leadId);

      if (removed) {
        removeLeadFromList(leadId);
        setArchivedLeads((prev) => prev.filter((l) => l.id !== leadId));
        setActionsLeadId(null);
        if (detailLeadId === leadId) {
          setDetailLeadId(null);
          if (searchParams.get('leadId')) setSearchParams({});
        }
        return;
      }

      const { data, error: fetchError } = await leadsService.getLeadById(leadId, authOpts);
      if (!fetchError && data) {
        const mapped = mapLeadFromRow(data);
        if (mapped.archived) {
          removeLeadFromList(leadId);
          setArchivedLeads((prev) => {
            const exists = prev.some((l) => l.id === leadId);
            if (exists) {
              return prev.map((l) => (l.id === leadId ? mapped : l));
            }
            return [...prev, mapped];
          });
        } else {
          upsertLeadInList(mapped);
          setArchivedLeads((prev) => prev.filter((l) => l.id !== leadId));
        }
      } else {
        await syncLeadInPipelineList(leadId);
      }
    },
    [
      authOpts,
      detailLeadId,
      invalidateLeadQueries,
      removeLeadFromList,
      searchParams,
      setSearchParams,
      syncLeadInPipelineList,
      upsertLeadInList
    ]
  );

  useEffect(() => {
    if (!showArchived || !profile?.id || !profile?.role) {
      if (!showArchived) setArchivedLeads([]);
      return;
    }
    let isActive = true;
    setArchivedLoading(true);
    leadsService
      .listLeads({
        role: profile.role,
        userId: profile.id,
        includeArchived: true,
        archivedOnly: true
      })
      .then(({ data, error: err }) => {
        if (!isActive) return;
        if (err) {
          toast.error(err.message ?? 'Failed to load archived leads');
          setArchivedLeads([]);
        } else {
          setArchivedLeads((Array.isArray(data) ? data : []).map(mapLeadFromRow));
        }
      })
      .finally(() => {
        if (isActive) setArchivedLoading(false);
      });
    return () => { isActive = false; };
  }, [showArchived, profile?.id, profile?.role]);

  useEffect(() => {
    leadsService.saveLeadPipelineFiltersToSession(pipelineFilters);
  }, [pipelineFilters]);

  const { events: distinctEventOptions, agentIds: distinctAgentIds } = useMemo(
    () => leadsService.getDistinctLeadFilterOptions(userLeads),
    [userLeads]
  );

  useEffect(() => {
    if (role !== 'admin' || !profile?.id) {
      setAgentFilterOptions([]);
      return;
    }
    if (distinctAgentIds.length === 0) {
      setAgentFilterOptions([]);
      return;
    }

    let isActive = true;
    setAgentsFilterLoading(true);
    leadsService
      .listAgentsForLeadFilter(distinctAgentIds, {
        role: profile.role ?? role,
        userId: profile.id
      })
      .then(({ data, error: err }) => {
        if (!isActive) return;
        if (err) {
          console.error('[LeadPipeline] Agent filter options failed:', err);
          setAgentFilterOptions([]);
        } else {
          setAgentFilterOptions(Array.isArray(data) ? data : []);
        }
      })
      .finally(() => {
        if (isActive) setAgentsFilterLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [role, profile?.id, profile?.role, distinctAgentIds.join(',')]);

  const filteredLeads = useMemo(
    () => leadsService.applyLeadPipelineFilters(userLeads, pipelineFilters, events),
    [userLeads, pipelineFilters, events]
  );

  const statusTab = pipelineFilters.leadStatus || 'all';

  const handleStatusTab = (status) => {
    setPipelineFilters((prev) => ({
      ...prev,
      leadStatus: status === 'all' ? '' : status
    }));
  };

  const handleClearPipelineFilters = () => {
    setPipelineFilters(getDefaultLeadPipelineFilters());
  };

  const getEventTitle = (lead) => {
    const eventId = lead?.eventId ?? lead?.event_id;
    const fromJoin = lead?.eventTitle ?? lead?.events?.title;
    if (fromJoin) return fromJoin;
    const event = events.find(e => e.id === eventId);
    return event ? event.title : 'Unknown Event';
  };

  const handleStatusChange = async (leadId, newStatus) => {
    setUpdating(leadId);
    try {
      await updateLead(leadId, { status: newStatus });
      toast.success('Status updated successfully');
    } catch (error) {
      console.error('[LeadPipeline] Status update failed:', error);
      toast.error(`Failed to update status: ${error.message}`);
    } finally {
      setUpdating(null);
    }
  };

  const openLeadActions = (lead) => {
    setActionsLeadId(lead.id);
  };

  const closeLeadActions = () => {
    setActionsLeadId(null);
  };

  const handleLogMeeting = (lead) => {
    setSelectedLead(lead);
    setShowMeetingModal(true);
    setMeetingDate('');
    setMeetingNotes('');
  };

  const handleSaveMeeting = async () => {
    if (!meetingDate || !meetingNotes) {
      toast.error('Please fill in both meeting date and notes');
      return;
    }

    if (!selectedLead?.id) {
      toast.error('Unable to log meeting without a lead.');
      return;
    }

    const leadId = selectedLead.id;
    const authRole = profile?.role ?? role;
    const authUserId = profile?.id ?? user?.id;

    setUpdating(leadId);
    try {
      const { error: activityError } = await leadsService.createLeadActivity(
        {
          lead_id: leadId,
          activity_type: 'meeting',
          activity_date: meetingDate,
          notes: meetingNotes,
          duration_minutes: null
        },
        { role: authRole, userId: authUserId }
      );
      if (activityError) {
        throw activityError;
      }

      const { error: leadError } = await leadsService.updateLead(
        leadId,
        { status: '1_1_completed' },
        { role: authRole, userId: authUserId }
      );

      if (leadError) {
        throw leadError;
      }

      if (profile?.id) {
        await fetchDashboardData(profile.id, profile?.role ?? role);
      }
      toast.success('Meeting logged successfully');
      setShowMeetingModal(false);
      setSelectedLead(null);
      setMeetingDate('');
      setMeetingNotes('');
    } catch (error) {
      console.error('[LeadPipeline] Meeting save failed:', error);
      toast.error(`Failed to log meeting: ${error.message}`);
    } finally {
      setUpdating(null);
    }
  };

  const statusColors = {
    new: 'bg-blue-100 text-blue-800',
    contacted: 'bg-yellow-100 text-yellow-800',
    '1_1_completed': 'bg-purple-100 text-purple-800',
    converted: 'bg-green-100 text-green-800',
    lost: 'bg-red-100 text-red-800'
  };

  const statusCounts = {
    new: userLeads.filter(l => l.status === 'new').length,
    contacted: userLeads.filter(l => l.status === 'contacted').length,
    '1_1_completed': userLeads.filter(l => l.status === '1_1_completed').length,
    converted: userLeads.filter(l => l.status === 'converted').length,
    lost: userLeads.filter(l => l.status === 'lost').length
  };

  const isLoadingList = loading || (showArchived && archivedLoading);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
        <p className="text-slate-600 font-medium">Loading dashboard data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
        <h2 className="text-lg font-semibold">We couldn&apos;t load your dashboard.</h2>
        <p className="mt-2 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lead Pipeline</h1>
          <p className="text-gray-600 mt-1">Track and manage your event registrations</p>
        </div>
        <button
          type="button"
          onClick={() => setExportModalOpen(true)}
          disabled={filteredLeads.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SafeIcon icon={FiDownload} className="w-4 h-4" />
          Export
        </button>
      </div>

      {/* Stats Overview (only when viewing active leads) */}
      {!showArchived && (
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {[
          { status: 'new', label: 'New Leads', icon: FiUsers, color: 'bg-blue-500' },
          { status: 'contacted', label: 'Contacted', icon: FiMail, color: 'bg-yellow-500' },
          { status: '1_1_completed', label: '1-1 Completed', icon: FiCheckCircle, color: 'bg-purple-500' },
          { status: 'converted', label: 'Converted', icon: FiCheck, color: 'bg-green-500' },
          { status: 'lost', label: 'Lost', icon: FiX, color: 'bg-red-500' }
        ].map((item, index) => (
          <motion.div
            key={item.status}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{item.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{statusCounts[item.status]}</p>
              </div>
              <div className={`${item.color} rounded-lg p-3`}>
                <SafeIcon icon={item.icon} className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">View:</span>
              <button
                type="button"
                onClick={() => setShowArchived(false)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${!showArchived ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Active
              </button>
              <button
                type="button"
                onClick={() => setShowArchived(true)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${showArchived ? 'bg-amber-100 text-amber-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Archived
              </button>
            </div>
            {!showArchived && (
            <div className="flex space-x-4 overflow-x-auto">
            {['all', 'new', 'contacted', '1_1_completed', 'converted', 'lost'].map((status) => (
              <button
                key={status}
                onClick={() => handleStatusTab(status)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                  statusTab === status
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {status === '1_1_completed' ? '1-1 Completed' : status.charAt(0).toUpperCase() + status.slice(1)}
                {status !== 'all' && (
                  <span className="ml-2 bg-gray-200 text-gray-600 px-2 py-1 rounded-full text-xs">
                    {statusCounts[status] || 0}
                  </span>
                )}
              </button>
            ))}
            </div>
            )}
          </div>
        </div>

        <LeadPipelineFilterBar
          filters={pipelineFilters}
          onFiltersChange={setPipelineFilters}
          onClear={handleClearPipelineFilters}
          eventOptions={distinctEventOptions}
          agentOptions={agentFilterOptions}
          showAgentFilter={role === 'admin'}
          agentsLoading={agentsFilterLoading}
        />

        {/* Leads Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  Source Link
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registered
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment / Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pipeline
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLeads.map((lead, index) => {
                const paymentBadge = getLeadPaymentBadge(lead, events);
                return (
                <motion.tr
                  key={lead.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4">
                    <div>
                      <button
                        type="button"
                        onClick={() => openLeadDetails(lead, 'details')}
                        className="font-medium text-gray-900 text-left cursor-pointer hover:text-blue-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                      >
                        {lead.fullName || lead.name}
                      </button>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiMail} className="w-4 h-4" />
                          <span>{lead.email}</span>
                        </div>
                        {lead.phone && (
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiPhone} className="w-4 h-4" />
                            <span>{lead.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900">{getEventTitle(lead)}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600">
                      {lead.sharingLinkSlug ? (
                        <span className="font-medium text-gray-900" title={lead.sharingLinkIdentifier ? `Identifier: ${lead.sharingLinkIdentifier}` : undefined}>
                          {lead.sharingLinkSlug}
                        </span>
                      ) : (
                        <span className="text-gray-400">Direct</span>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {format(new Date(lead.registeredAt), 'MMM dd, yyyy')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${paymentBadge.className}`}>
                        {paymentBadge.label}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${lead.accountCreated ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'}`}>
                        {lead.accountCreated ? 'Account Created ✓' : 'No Account'}
                      </span>
                      {lead.studentId && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800">
                          Registered
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {showArchived ? (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border-0 ${statusColors[lead.status] ?? 'bg-gray-100 text-gray-800'}`}>
                        {lead.status === '1_1_completed' ? '1-1 Completed' : (lead.status ?? 'new')}
                      </span>
                    ) : (
                      <select
                        value={lead.status}
                        onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border-0 ${statusColors[lead.status]}`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="1_1_completed">1-1 Completed</option>
                        <option value="converted">Converted</option>
                        <option value="lost">Lost</option>
                      </select>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => openLeadActions(lead)}
                        className="text-gray-400 hover:text-blue-600 transition-colors"
                        title="Lead actions"
                      >
                        <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                      </button>
                      {!showArchived && (
                        <button
                          type="button"
                          onClick={() => handleLogMeeting(lead)}
                          className="text-gray-400 hover:text-purple-600 transition-colors"
                          title="Log 1-1 Meeting"
                        >
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredLeads.length === 0 && (
          <div className="p-12 text-center">
            <SafeIcon icon={FiUsers} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">
              {leadsService.countActiveLeadPipelineFilters(pipelineFilters) > 0
                ? 'No leads match your filters'
                : showArchived
                  ? 'No archived leads'
                  : 'No leads found'}
            </p>
            <p className="text-gray-400">
              {leadsService.countActiveLeadPipelineFilters(pipelineFilters) > 0 ? (
                <button
                  type="button"
                  onClick={handleClearPipelineFilters}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear filters
                </button>
              ) : showArchived ? (
                'Archived leads will appear here when you archive leads from the pipeline'
              ) : (
                'Leads will appear here as people register for your events'
              )}
            </p>
          </div>
        )}
      </div>

      <LeadDetailModal
        leadId={detailLeadId}
        isOpen={Boolean(detailLeadId) && !showMeetingModal}
        onClose={closeLeadDetails}
        onStatusChange={handleLeadDetailStatusChange}
        defaultTab={detailDefaultTab}
      />

      <LeadActionsModal
        leadId={actionsLeadId}
        isOpen={Boolean(actionsLeadId)}
        onClose={closeLeadActions}
        onActionComplete={handleLeadActionComplete}
      />

      {/* 1-1 Meeting Modal */}
      {showMeetingModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Log 1-1 Meeting
              </h3>
              <button
                onClick={() => {
                  setShowMeetingModal(false);
                  setSelectedLead(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>{selectedLead.fullName ?? selectedLead.name ?? '—'}</strong> — {selectedLead.email ?? '—'}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Date *
                </label>
                <input
                  type="date"
                  value={meetingDate}
                  onChange={(e) => setMeetingDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Notes *
                </label>
                <textarea
                  value={meetingNotes}
                  onChange={(e) => setMeetingNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="What was discussed during the 1-1 meeting?"
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowMeetingModal(false);
                  setSelectedLead(null);
                }}
                className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveMeeting}
                className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Save Meeting
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <ExportLeadsModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        leads={filteredLeads}
        events={events}
        role={profile?.role ?? role}
        userId={profile?.id ?? user?.id}
      />
    </div>
  );
};

export default LeadPipeline;
